/**
 * Whether a drawn signature is a signature (SCOPE §11.1, decision 127).
 *
 * The pad records path data — `M x y L x y …` — in a fixed 600 × 160 space.
 * "Has a line in it" used to be the test, and a real tap always jitters a
 * pixel or two, so one tap read as signed and *Take it in* lit up (FB-1095).
 *
 * Two measures, both needed:
 *
 *  - **ink**: how far the pen actually travelled while down, summed over every
 *    stroke. One tap, however jittery, is a pixel or two.
 *  - **span**: how far across the box the marks reach. Several taps scattered
 *    across the box reach a long way and carry no ink.
 *
 * A signature has both. A deliberate mark — an X, initials — still counts:
 * that is a legal mark, and the printed name typed beside it is the record for
 * anyone who cannot draw one.
 *
 * Shared by the pad, which decides when to say "Signed", and the server, which
 * refuses anything less, so the two can never disagree.
 */

export const SIGNATURE_WIDTH = 600;
export const SIGNATURE_HEIGHT = 160;

/** Total pen travel, in the 600-wide space: a tenth of the box. */
export const MIN_SIGNATURE_INK = 60;
/** How far the marks must reach, across or down. */
export const MIN_SIGNATURE_SPAN = 30;

export interface SignatureMeasure {
  readonly ink: number;
  readonly span: number;
}

/** Ink and span of `M x y L x y …` path data. Anything unreadable measures nothing. */
export function measureSignature(path: string): SignatureMeasure {
  const tokens = path
    .trim()
    .split(/[\s,]+/)
    .filter((token) => token !== '');
  let ink = 0;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  let previous: { x: number; y: number } | null = null;

  for (let index = 0; index < tokens.length; index += 3) {
    const command = tokens[index];
    const x = Number(tokens[index + 1]);
    const y = Number(tokens[index + 2]);
    if ((command !== 'M' && command !== 'L') || !Number.isFinite(x) || !Number.isFinite(y)) {
      return { ink: 0, span: 0 };
    }
    if (command === 'L' && previous !== null) {
      ink += Math.hypot(x - previous.x, y - previous.y);
    }
    previous = { x, y };
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  if (previous === null) return { ink: 0, span: 0 };
  return { ink, span: Math.max(maxX - minX, maxY - minY) };
}

/** True when the path is a signature, not a dot or a scatter of taps. */
export function isSigned(path: string): boolean {
  const { ink, span } = measureSignature(path);
  return ink >= MIN_SIGNATURE_INK && span >= MIN_SIGNATURE_SPAN;
}
